export const IMG = {
  founder: "/img/founder.jpg",
  founderWide: "/img/founder-wide.jpg",
  ebook: "/img/ebook.jpg",
  ebookTall: "/img/ebook-tall.jpg",
  workbook: "/img/workbook.jpg",
  bundle: "/img/bundle.jpg",
  bundleSquare: "/img/bundle-square.jpg",
};

export const PRODUCTS = {
  ebook: {
    role: "ebook",
    kind: "eBook",
    name: "The First Mover Blueprint",
    price: 89,
    image: IMG.ebook,
    tagline: "The Blueprint for spotting hidden signals and seizing market opportunities, before your competition even sees them coming.",
    includes: [
      "Chapter 1, Kickstarting Your Trend-Seizing Journey: not just identifying market trends, but acting on them before your competition sees them coming",
      "From insight to execution: choose one specific opportunity, plan your next steps and start acting, from a pilot project to a new service",
      "Chapter 2, Building a Signal-Driven Mindset: know when to react to important cues and when to ignore distracting noise",
      "Understanding Market Data Noise: filter out random fluctuations and short-term spikes to hear the clear, actionable signals",
      "Chapter 4, Performance Feedback and Refinement: review which signals you acted on, learn from every result and keep separating genuine opportunities from fleeting noise",
    ],
  },
  workbook: {
    role: "workbook",
    kind: "Workbook",
    name: "The First Mover Workbook",
    price: 40,
    image: IMG.workbook,
    tagline: "180 pages of guided worksheets that turn every chapter into decisions, dates and next actions.",
    includes: [
      "11 guided worksheets, from your Personal Roadmap Planner to the Innovation Scaling Planner",
      "Signal Assessment Checklist and Urgency Assessment Worksheet to score a trend before you invest",
      "Action Planning Blueprint to turn one insight into concrete steps, owners and deadlines",
      "Daily Integration Action Plan and Adaptive Strategy Tracker so the method becomes a habit",
      "Step-by-step prompts and worked examples on every page, no blank-page panic",
    ],
  },
  bundle: {
    role: "bundle",
    kind: "Bundle",
    name: "First Mover OS: The Complete System",
    price: 129,
    installment: 64.5,
    image: IMG.bundle,
    tagline: "The eBook and the Workbook together: learn the method and apply it in the same week.",
    includes: [
      "The First Mover Blueprint eBook: 6 chapters, 25 lessons",
      "The 180-page First Mover Workbook with 11 guided worksheets",
      "Read a lesson, then apply it straight away in the matching worksheet",
      "Option to pay in 2 equal monthly instalments",
      "Covered by the 7-day money-back guarantee",
    ],
  },
};

export const STATS = [
  { value: 6, label: "Chapters" },
  { value: 25, label: "Lessons" },
  { value: 11, label: "Worksheets" },
  { value: 180, label: "Workbook pages" },
];

export const METHOD = [
  { step: "Exploration", title: "Scan wide.", text: "Gain early insights from diverse sources: industry news, consumer behaviour and the global shifts that hint at future trends." },
  { step: "Analysis", title: "Filter the noise.", text: "Separate random spikes from real signals. Score signal strength and urgency, so you only act on trends with lasting impact." },
  { step: "Execution", title: "Move first.", text: "Turn one strong signal into a concrete move: a pilot, a new offer, a shift in focus. Then review, refine and repeat." },
];

export const CHAPTERS = [
  { n: 1, title: "Kickstarting Your Trend-Seizing Journey", outcome: "Your roadmap from first insight to actionable market strategy.",
    lessons: ["Overview and Roadmap", "Objectives and Expected Outcomes", "Maximizing Your eBook Experience"] },
  { n: 2, title: "Identifying Actionable Market Signals", outcome: "Hear the clear signal underneath the market static.",
    lessons: ["Understanding Market Data Noise", "Recognizing Lucrative Trends", "Evaluating Signal Strength", "Leveraging Analytical Tools", "Building a Signal-Driven Mindset"] },
  { n: 3, title: "Framework Adoption", outcome: "Spot the faint clues and urgency triggers before they are obvious to everyone.",
    lessons: ["Decoding Subtle Market Clues", "Urgency Triggers in Trends", "Integrating Signals with Urgency", "Framework Testing with Market Scenarios"] },
  { n: 4, title: "Action Activation", outcome: "Transform analysis into decisive market moves.",
    lessons: ["Action Diagnostics: Evaluating Signals for Quick Decisions", "From Trend to Tactic: Building Immediate Momentum", "Swift Decision Tactics: Mastering Rapid Execution", "Performance Feedback: Refining Tactics for Continued Success"] },
  { n: 5, title: "Market Mastery & Sustainment", outcome: "Make the framework part of how you work every day.",
    lessons: ["Reviewing Your Continuous Framework", "Deep Dive into Daily Operation Integration", "Real-Time Adaptation Strategies", "Leadership in Continuous Improvement"] },
  { n: 6, title: "Advanced Mastery for Trend Dominance", outcome: "Forecast further ahead, then scale what works.",
    lessons: ["Refining Your Trend Framework", "Integrating Feedback Loops", "Advanced Trend Forecasting", "Sustainable Market Leadership", "Scaling Innovation and Expansion"] },
];

export const WORKSHEETS = [
  { phase: "Plan", items: [
    { title: "Personal Roadmap Planner", text: "Milestones, target dates and checkpoints for your whole journey." },
    { title: "Outcome Mapping Worksheet", text: "A clear path from your ideas to real market results." },
    { title: "Study & Schedule Organizer", text: "Weekly goals and focused sessions that fit around your life." },
  ] },
  { phase: "Spot & score", items: [
    { title: "Urgency Assessment Worksheet", text: "Score the signals that say it is time to act fast." },
    { title: "Signal Assessment Checklist", text: "Quickly decide which signals deserve your attention." },
  ] },
  { phase: "Act", items: [
    { title: "Action Planning Blueprint", text: "Turn an emerging insight into steps, owners and deadlines." },
    { title: "Daily Integration Action Plan", text: "Bring the framework into your everyday tasks." },
    { title: "Adaptive Strategy Tracker", text: "Track market changes and adjust your tactics quickly." },
  ] },
  { phase: "Refine & scale", items: [
    { title: "Framework Enhancement Blueprint", text: "Find the gaps in your framework and upgrade it." },
    { title: "Leadership Sustainability Analysis", text: "Review how you lead and keep improving." },
    { title: "Innovation Scaling Planner", text: "Map out how to scale what works." },
  ] },
];

export const EXCERPT = {
  chapter: "Chapter 2 · Evaluating Signal Strength",
  text: "Imagine you’re tuning into a distant radio station with static and murmurs all around. At first, every sound may seem promising, but only when you fine-tune the dial do you catch the clear melody hidden beneath. The clear melody is the strong trend, and the static represents the weak, unreliable signals you want to ignore.",
};

export const STORY_LINES = [
  "I started with dropshipping. Like everyone else.",
  "I copied what was already trending and lost money, because everyone was already there.",
  "So I stopped chasing trends and learned to spot the shift before it peaks.",
  "That system became the First Mover Method.",
];

export const PAINS = [
  {
    title: "You want out, but you have no idea what to sell.",
    text: "You know you want your own thing. But every time you sit down to start, the page stays blank and the doubt gets louder.",
  },
  {
    title: "Every opportunity you find is already crowded.",
    text: "By the time an idea reaches your feed, thousands of people are selling it. You arrive late, compete on price and give up.",
  },
  {
    title: "Hours of free content. Still not started.",
    text: "Videos, threads, podcasts. You know more than ever and have built nothing, because nobody showed you a clear order of steps.",
  },
];

export const FAQS = [
  {
    q: "I have no business and no experience. Is this for me?",
    a: "Yes. The method is written for complete beginners. You do not need a product, an audience or a budget to start.",
  },
  {
    q: "What exactly is inside?",
    a: "The eBook has 6 chapters and 25 lessons, from reading market noise to scaling what works. The Workbook has 180 pages with 11 guided worksheets, each with step-by-step prompts and examples.",
  },
  {
    q: "What is the difference between the eBook and the Bundle?",
    a: "The eBook teaches the method. The Bundle adds the 180-page Workbook, where you apply every step with 11 guided worksheets. If you want to act, not just read, take the Bundle.",
  },
  {
    q: "What if it does not work for me?",
    a: "Then you get your money back. You have 7 days to go through the material. If it is not right for you, email us and we refund you in full. No questions, no hoops.",
  },
  {
    q: "How do I receive the products?",
    a: "Instantly after payment. You receive a download link by email and can read on any device.",
  },
  {
    q: "Can I pay in instalments?",
    a: "Yes, the Bundle is available in 2 equal monthly instalments. Klarna, PayPal and Shop Pay are also available at checkout.",
  },
  {
    q: "How much time do I need?",
    a: "The material is designed for people with a job or a family. Most worksheets take between 5 and 60 minutes, so a few focused hours per week are enough to move forward.",
  },
];

export const MARQUEE = ["Filter the noise", "Spot the signal", "Move first", "Exploration", "Analysis", "Execution", "Start before you feel ready"];

export const NAV = [
  { to: "/", label: "Method" },
  { to: "/products", label: "Products" },
  { to: "/about", label: "About Dilara" },
  { to: "/support", label: "FAQ" },
  { to: "/blog", label: "Blog" },
];
